import type { AgentTurn } from "./agentTypes";
import { deriveTitle } from "./conversationStore";

export type ConversationPillStatus = "idle" | "working" | "waitingForYou" | "failed";

export type ConversationPill = {
  title: string;
  messageCount: number;
  status: ConversationPillStatus;
  conversationKey: string;
};

const messagesIn = (turn: AgentTurn): number =>
  (turn.user || turn.startButtonLabel || turn.attachments?.length ? 1 : 0) + (turn.answerText ? 1 : 0);

const statusOf = (turns: AgentTurn[], isRunning: boolean, awaitsConfirmation: boolean): ConversationPillStatus => {
  if (awaitsConfirmation) return "waitingForYou";
  if (isRunning) return "working";
  return turns[turns.length - 1]?.failed ? "failed" : "idle";
};

export type StepHelpNaming = {
  buttonLabel: string;
  stepName: string | undefined;
  titleFor: (stepName: string) => string;
};

const stepHelpTitle = (first: AgentTurn, stepHelp: StepHelpNaming | undefined): string | undefined =>
  stepHelp?.stepName && first.startButtonLabel === stepHelp.buttonLabel ? stepHelp.titleFor(stepHelp.stepName) : undefined;

export const conversationPill = ({
  turns,
  isRunning,
  awaitsConfirmation = false,
  stepHelp,
}: {
  turns: AgentTurn[];
  isRunning: boolean;
  awaitsConfirmation?: boolean;
  stepHelp?: StepHelpNaming;
}): ConversationPill | null => {
  const first = turns[0];
  if (!first) return null;
  const opening = first.user || first.attachments?.[0]?.fileName;
  return {
    title: stepHelpTitle(first, stepHelp) ?? first.startButtonLabel ?? deriveTitle(opening),
    conversationKey: `${first.startButtonLabel ?? ""}\u0000${opening ?? ""}`,
    messageCount: turns.reduce((count, turn) => count + messagesIn(turn), 0),
    status: statusOf(turns, isRunning, awaitsConfirmation),
  };
};
